import { type SalesMetricV2 } from "./types";

export function makeTiktokVideoViews(): SalesMetricV2 {
  const id = "tiktokVideoViews";
  const name = "TikTok Video Views";

  return {
    id,
    name,
    fullName: name,
    description:
      "The number of times your video starts to play. Replays will not be counted",
    category: "tiktok",
    positiveTrend: "up",
    format: "integer",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeTiktokVideoViews2s(): SalesMetricV2 {
  const id = "tiktokVideoViews2s";
  const name = "TikTok Two-second Video Views";

  return {
    id,
    name,
    fullName: name,
    description:
      "Number of times your video was played for at least 2 seconds. Replays will not be counted",
    category: "tiktok",
    positiveTrend: "up",
    format: "integer",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeTiktokVideoViews6s(): SalesMetricV2 {
  const id = "tiktokVideoViews6s";
  const name = "TikTok Six-second Video Views";

  return {
    id,
    name,
    fullName: name,
    description:
      "Number of times your video was played for at least 6 seconds. Replays will not be counted",
    category: "tiktok",
    positiveTrend: "up",
    format: "integer",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeTiktokVideoViewsP100(): SalesMetricV2 {
  const id = "tiktokVideoViewsP100";
  const name = "TikTok P100 Video Views";

  return {
    id,
    name,
    fullName: name,
    description:
      "The number of times your video was played at 100% of its length. Replays will not be counted",
    category: "tiktok",
    positiveTrend: "up",
    format: "integer",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeTiktokVideoViewsP75(): SalesMetricV2 {
  const id = "tiktokVideoViewsP75";
  const name = "TikTok P75 Video Views";

  return {
    id,
    name,
    fullName: name,
    description:
      "The number of times your video was played at 75% of its length. Replays will not be counted",
    category: "tiktok",
    positiveTrend: "up",
    format: "integer",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeTiktokVideoViewsP50(): SalesMetricV2 {
  const id = "tiktokVideoViewsP50";
  const name = "TikTok P50 Video Views";

  return {
    id,
    name,
    fullName: name,
    description:
      "The number of times your video was played at 50% of its length. Replays will not be counted",
    category: "tiktok",
    positiveTrend: "up",
    format: "integer",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeTiktokVideoViewsP25(): SalesMetricV2 {
  const id = "tiktokVideoViewsP25";
  const name = "TikTok P25 Video Views";

  return {
    id,
    name,
    fullName: name,
    description:
      "The number of times your video was played at 25% of its length. Replays will not be counted",
    category: "tiktok",
    positiveTrend: "up",
    format: "integer",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeTiktokImprs(): SalesMetricV2 {
  const id = "tiktokImprs";
  const name = "TikTok Impressions";

  return {
    id,
    name,
    fullName: name,
    description: "The number of times your ads were on screen",
    category: "tiktok",
    positiveTrend: "up",
    format: "integer",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeTiktokClicks(): SalesMetricV2 {
  const id = "tiktokClicks";
  const name = "TikTok Clicks";

  return {
    id,
    name,
    fullName: name,
    description:
      "The number of clicks on your ads that led users to advertiser-specified destinations " +
      "like websites, apps, or to the app store",
    category: "tiktok",
    positiveTrend: "up",
    format: "integer",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeTiktokLikes(): SalesMetricV2 {
  const id = "tiktokLikes";
  const name = "TikTok Paid Likes";

  return {
    id,
    name,
    fullName: name,
    description:
      "The number of likes your video creative received within 1 day of a user seeing a paid ad",
    category: "tiktok",
    positiveTrend: "up",
    format: "integer",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeTiktokCompletePayment(): SalesMetricV2 {
  const id = "tiktokCompletePayment";
  const name = "TikTok Complete Payments";

  return {
    id,
    name,
    fullName: name,
    description: "The total number of purchases attributed to TikTok spend",
    category: "tiktok",
    positiveTrend: "up",
    format: "integer",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeTiktokTotalCompletePayment(): SalesMetricV2 {
  const id = "tiktokTotalCompletePayment";
  const name = "TikTok Complete Payment Revenue";

  return {
    id,
    name,
    fullName: name,
    description: "The total value returned from each Complete Payment event",
    category: "tiktok",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeTiktokEngagements(): SalesMetricV2 {
  const id = "tiktokEngagements";
  const name = "TikTok Total Engagement";

  return {
    id,
    name,
    fullName: name,
    description:
      "The overall amount of engagement on your ad that includes paid likes, comments, shares, " +
      /* eslint-disable-next-line quotes */
      'follows, and clicks. Clicks in the "Total Engagement" metric also include music, ' +
      "hashtag, anchor, and interactive add-on clicks.",
    category: "tiktok",
    positiveTrend: "up",
    format: "integer",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeTiktokCPM(): SalesMetricV2 {
  const id = "tiktokCPM";
  const name = "TikTok CPM";

  return {
    id,
    name,
    fullName: name,
    description: "TikTok reported CPM",
    category: "tiktok",
    positiveTrend: "down",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeTiktokCTR(): SalesMetricV2 {
  const id = "tiktokCTR";
  const name = "TikTok CTR";

  return {
    id,
    name,
    fullName: name,
    description: "TikTok CTR",
    category: "tiktok",
    positiveTrend: "up",
    format: "percentage",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeTiktokCPC(): SalesMetricV2 {
  const id = "tiktokCPC";
  const name = "TikTok CPC";

  return {
    id,
    name,
    fullName: name,
    description: "TikTok CPC",
    category: "tiktok",
    positiveTrend: "down",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeTiktokROAS(): SalesMetricV2 {
  const id = "tiktokROAS";
  const name = "TikTok ROAS";

  return {
    id,
    name,
    fullName: name,
    description:
      "Calculated by taking the total revenue reported by tiktok and dividing by spend",
    category: "tiktok",
    positiveTrend: "up",
    format: "multiplier",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeTiktokCAC(): SalesMetricV2 {
  const id = "tiktokCAC";
  const name = "TikTok CAC";

  return {
    id,
    name,
    fullName: name,
    description:
      "Calculated by taking the total spend reported by TikTok and dividing by total " +
      "number of purchases",
    category: "tiktok",
    positiveTrend: "down",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeTiktokThumbstopRate(): SalesMetricV2 {
  const id = "tiktokThumbstopRate";
  const name = "TikTok Thumbstop Rate";

  return {
    id,
    name,
    fullName: name,
    description:
      "Thumbstop rate is calculated by dividing 2-second video views by impressions",
    category: "tiktok",
    positiveTrend: "up",
    format: "percentage",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeTiktokGMVNetCost(): SalesMetricV2 {
  const id = "tiktokGMVNetCost";
  const name = "TikTok GMV Net Cost";

  return {
    id,
    name,
    fullName: name,
    description:
      "The Net Cost (Delayed) metric is a feature in TikTok Ads Manager that provides " +
      "advertisers with the true cost of their campaigns after adjusting for ad credits or coupons used",
    category: "tiktok",
    positiveTrend: "down",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}
export function makeTiktokGMVGrossRevenue(): SalesMetricV2 {
  const id = "tiktokGMVGrossRevenue";
  const name = "TikTok GMV Gross Revenue";

  return {
    id,
    name,
    fullName: name,
    description: "The gross revenue from TikTok GMV",
    category: "tiktok",
    positiveTrend: "up",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeTiktokGMVCostPerLiveView(): SalesMetricV2 {
  const id = "tiktokGMVCostPerLiveView";
  const name = "TikTok GMV Cost Per Live View";

  return {
    id,
    name,
    fullName: name,
    description: "The cost per live view for TikTok GMV",
    category: "tiktok",
    positiveTrend: "down",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}
export function makeTiktokGMVCostPer10SecondLiveView(): SalesMetricV2 {
  const id = "tiktokGMVCostPer10SecondLiveView";
  const name = "TikTok GMV Cost Per 10 Second Live View";

  return {
    id,
    name,
    fullName: name,
    description: "The cost per 10 second live view for TikTok GMV",
    category: "tiktok",
    positiveTrend: "down",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}
export function makeTiktokGMVOrders(): SalesMetricV2 {
  const id = "tiktokGMVOrders";
  const name = "TikTok GMV Orders";

  return {
    id,
    name,
    fullName: name,
    description: "The number of orders from TikTok GMV",
    category: "tiktok",
    positiveTrend: "up",
    format: "integer",
    getValue(metrics) {
      return metrics[id];
    },
  };
}

export function makeTiktokGMVLiveViews(): SalesMetricV2 {
  const id = "tiktokGMVLiveViews";
  const name = "TikTok GMV Live Views";

  return {
    id,
    name,
    fullName: name,
    description: "The number of live views from TikTok GMV",
    category: "tiktok",
    positiveTrend: "up",
    format: "integer",
    getValue(metrics) {
      return metrics[id];
    },
  };
}
export function makeTiktokGMVTenSecondLiveViews(): SalesMetricV2 {
  const id = "tiktokGMVTenSecondLiveViews";
  const name = "TikTok GMV 10 Second Live Views";

  return {
    id,
    name,
    fullName: name,
    description: "The number of 10 second live views for TikTok GMV",
    category: "tiktok",
    positiveTrend: "up",
    format: "integer",
    getValue(metrics) {
      return metrics[id];
    },
  };
}
export function makeTiktokGMVLiveFollows(): SalesMetricV2 {
  const id = "tiktokGMVLiveFollows";
  const name = "TikTok GMV Live Follows";

  return {
    id,
    name,
    fullName: name,
    description: "The number of live follows from TikTok GMV",
    category: "tiktok",
    positiveTrend: "up",
    format: "integer",
    getValue(metrics) {
      return metrics[id];
    },
  };
}
export function makeTiktokGMVCostPerOrder(): SalesMetricV2 {
  const id = "tiktokGMVCostPerOrder";
  const name = "TikTok GMV Cost Per Order";

  return {
    id,
    name,
    fullName: name,
    description: "The cost per order for TikTok GMV",
    category: "tiktok",
    positiveTrend: "down",
    format: "dollars",
    getValue(metrics) {
      return metrics[id];
    },
  };
}
